// Customers' Equipment (EquipmentPage.tsx): what it asks equipment.list for, and its answer as ui-web's
// EquipmentList props. The page's three views are the action's own filters: everything, a service due in
// the next 30 days (or overdue), a warranty ending in the next 60, each by kind when one is picked.
import {
  DUE_SOON_DAYS,
  WARRANTY_SOON_DAYS,
  type EquipmentListProps,
  type EquipmentRow,
} from '@flowology/ui-web';

export type EquipmentShow = 'all' | 'due' | 'warranty';

export const EQUIPMENT_SHOWS: { show: EquipmentShow; label: string; empty: string }[] = [
  {
    show: 'all',
    label: 'Everything',
    empty: 'No equipment on file yet. Technicians add it on the iPad when they see it.',
  },
  { show: 'due', label: 'Service due', empty: 'No service is due in the next 30 days.' },
  { show: 'warranty', label: 'Warranty ending', empty: 'No warranty ends in the next 60 days.' },
];

/** One page of equipment.list's answer (packages/modules tags/equipment-list.ts). */
export interface EquipmentPage {
  equipment: EquipmentRow[];
  next: string | null;
  kinds: { kind: string; count: number }[] | null;
  /** The first page only: every piece the filters take, counted by the server. */
  total?: number | null;
}

/** equipment.list's input for a view, a kind and a page. */
export function equipmentInput(
  show: EquipmentShow,
  kind: string | null,
  after: string | null = null,
): Record<string, unknown> {
  return {
    ...(show === 'due' ? { serviceDueWithinDays: DUE_SOON_DAYS } : {}),
    ...(show === 'warranty' ? { warrantyEndsWithinDays: WARRANTY_SOON_DAYS } : {}),
    ...(kind ? { kind } : {}),
    ...(after ? { after } : {}),
  };
}

/** What's due beside the list, whatever the list shows: the services due in the next 30 days. */
export const DUE_SIDE_INPUT = { serviceDueWithinDays: DUE_SOON_DAYS, limit: 100 };

/** The soonest due first (the overdue ones lead): equipment.list keeps its newest-first order. */
export const byDue = (rows: EquipmentRow[]): EquipmentRow[] =>
  [...rows].sort((a, b) => (a.serviceDue?.on ?? '').localeCompare(b.serviceDue?.on ?? ''));

/** The rows loaded so far, as EquipmentList draws them, on the company's today. */
export const equipmentListProps = (today: string, rows: EquipmentRow[]): EquipmentListProps => ({
  today,
  equipment: rows,
});

/** "Water heater (12)": a kind and how many there are, for the kind filter. */
export const kindLabel = (k: { kind: string; count: number }) => `${k.kind} (${k.count})`;

/** How many pieces are on record: the kinds are every kind (list caps L4). */
export function onRecord(kinds: { kind: string; count: number }[] | null | undefined): number | null {
  if (!kinds) return null;
  return kinds.reduce((n, k) => n + k.count, 0);
}

/**
 * A list's count beside its title: the server's over every page (list caps L3), or, from a server
 * without it, the rows read.
 */
export const listCount = (first: Pick<EquipmentPage, 'total'> | null, rows: readonly unknown[]): number =>
  first?.total ?? rows.length;
